// Single source of truth for the trader reward mechanics shown in the
// Performance dashboard. Mirrors the rules in the payout-rules canvas:
//   payout = $100 + 30 × NetR × (WR-mult + share-mult + referral-mult)
//            + engagement bonus
// All values here are estimates for trader self-tracking; real payouts are
// computed server-side.

export const SHARE_MIN = 5
export const SHARE_PER = 0.01
export const SHARE_CAP = 0.25

export const REFERRAL_MIN = 5
export const REFERRAL_PER = 0.02
export const REFERRAL_CAP = 0.4

export const POINT_VALUE_USD = 0.5
export const ENGAGEMENT_BONUS_CAP = 150

export const POINTS = {
  activeDay: 2,
  share: 3,
  takeProfit: 5,
  interaction: 1,
  streak7: 10,
}
export const INTERACTION_CAP = 20
export const STREAK_CAP = 4

export const BASE_PAYMENT_USD = 100
export const BONUS_PER_R_USD = 30

// Win-rate multiplier (kept in sync with the Performance bonus calculator).
export function getWRMultiplier(wr: number): number {
  if (wr >= 67) return 1.35
  if (wr >= 50) return 1.25
  if (wr >= 45) return 1.1
  if (wr >= 40) return 1.0
  if (wr >= 37) return 0.95
  if (wr >= 35) return 0.9
  if (wr >= 33) return 0.75
  return 0
}

// Each share adds SHARE_PER once the SHARE_MIN threshold is reached, capped.
export function getShareMultiplier(shares: number): number {
  if (shares < SHARE_MIN) return 0
  return Math.min(shares * SHARE_PER, SHARE_CAP)
}

// Each active referral adds REFERRAL_PER once REFERRAL_MIN is reached, capped.
export function getReferralMultiplier(activeReferrals: number): number {
  if (activeReferrals < REFERRAL_MIN) return 0
  return Math.min(activeReferrals * REFERRAL_PER, REFERRAL_CAP)
}

export type EngagementSignals = {
  activeDays: number
  shares: number
  takeProfits: number
  interactions: number
  streaks7: number
}

export type EngagementBreakdown = {
  activeDays: number
  shares: number
  takeProfits: number
  interactions: number
  streaks7: number
  total: number
}

export function computeEngagementPoints(signals: EngagementSignals): EngagementBreakdown {
  const activeDays = Math.max(0, signals.activeDays) * POINTS.activeDay
  const shares = Math.max(0, signals.shares) * POINTS.share
  const takeProfits = Math.max(0, signals.takeProfits) * POINTS.takeProfit
  const interactions =
    Math.min(Math.max(0, signals.interactions), INTERACTION_CAP) * POINTS.interaction
  const streaks7 = Math.min(Math.max(0, signals.streaks7), STREAK_CAP) * POINTS.streak7
  return {
    activeDays,
    shares,
    takeProfits,
    interactions,
    streaks7,
    total: activeDays + shares + takeProfits + interactions + streaks7,
  }
}

export function getEngagementBonus(points: number, eligible: boolean): number {
  if (!eligible) return 0
  return Math.min(points * POINT_VALUE_USD, ENGAGEMENT_BONUS_CAP)
}

// Count complete 7-consecutive-day runs across the active dates (capped).
// e.g. 16 consecutive active days → 2 streak bonuses.
export function computeStreaks7(dates: string[]): number {
  const sorted = Array.from(new Set(dates)).sort()
  if (sorted.length === 0) return 0
  let streaks = 0
  let run = 1
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1] + 'T00:00:00Z').getTime()
    const cur = new Date(sorted[i] + 'T00:00:00Z').getTime()
    const diffDays = Math.round((cur - prev) / 86400000)
    if (diffDays === 1) {
      run += 1
    } else {
      streaks += Math.floor(run / 7)
      run = 1
    }
  }
  streaks += Math.floor(run / 7)
  return Math.min(streaks, STREAK_CAP)
}

export type PayoutInput = {
  eligible: boolean
  netR: number
  wrMultiplier: number
  shareMultiplier: number
  referralMultiplier: number
  engagementBonus: number
}

export type PayoutBreakdown = {
  base: number
  effectiveMultiplier: number
  performanceBonus: number
  engagementBonus: number
  total: number
}

export function computePayout(input: PayoutInput): PayoutBreakdown {
  if (!input.eligible) {
    return {base: 0, effectiveMultiplier: 0, performanceBonus: 0, engagementBonus: 0, total: 0}
  }
  const effectiveMultiplier = input.wrMultiplier + input.shareMultiplier + input.referralMultiplier
  const performanceBonus = Math.max(0, BONUS_PER_R_USD * input.netR * effectiveMultiplier)
  return {
    base: BASE_PAYMENT_USD,
    effectiveMultiplier,
    performanceBonus,
    engagementBonus: input.engagementBonus,
    total: BASE_PAYMENT_USD + performanceBonus + input.engagementBonus,
  }
}

// Resolve the [start, end] calendar window for a dashboard period key, so we
// can filter referral invitations to the same window the rest of the page uses.
export function getPeriodRange(period: string): {start: Date; end: Date} {
  const now = new Date()
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
  const endOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)

  switch (period) {
    case 'today':
      return {start: startOfDay(now), end: endOfDay(now)}
    case 'yesterday': {
      const y = new Date(now)
      y.setDate(now.getDate() - 1)
      return {start: startOfDay(y), end: endOfDay(y)}
    }
    case 'this_week': {
      const day = (now.getDay() + 6) % 7 // Monday-based
      const start = new Date(now)
      start.setDate(now.getDate() - day)
      return {start: startOfDay(start), end: endOfDay(now)}
    }
    case 'last_week': {
      const day = (now.getDay() + 6) % 7
      const end = new Date(now)
      end.setDate(now.getDate() - day - 1)
      const start = new Date(end)
      start.setDate(end.getDate() - 6)
      return {start: startOfDay(start), end: endOfDay(end)}
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      return {start: startOfDay(start), end: endOfDay(end)}
    }
    case 'this_year': {
      const start = new Date(now.getFullYear(), 0, 1)
      return {start: startOfDay(start), end: endOfDay(now)}
    }
    case 'last_year': {
      const start = new Date(now.getFullYear() - 1, 0, 1)
      const end = new Date(now.getFullYear() - 1, 11, 31)
      return {start: startOfDay(start), end: endOfDay(end)}
    }
    case 'this_month':
    default: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      return {start: startOfDay(start), end: endOfDay(now)}
    }
  }
}

export function toISODate(d: Date): string {
  return d.toISOString().split('T')[0]
}
